/* Copyright (c) Trimble Inc. All rights reserved. */

import _ from 'lodash';
import clsx from 'clsx';
import type { ReactElement } from 'react';
import React from 'react';
import { Box, Grid, GridSize, Typography, useTheme, makeStyles } from '@material-ui/core';

// Define styles using makeStyles
const useStyles = makeStyles((theme) => ({
  titleCell: {
    border: '0.5px solid #B7B9C3',
    backgroundColor: '#F1F1F6',
  },
  valueCell: {
    padding: '15px',
    border: '0.5px solid #B7B9C3',
  },
  titleBox: {
    display: 'flex',
    flexDirection: 'row',
    padding: '15px',
  },
  borderedRow: {
    border: '0.5px solid #B7B9C3',
  },
  controlsContainer: {
    paddingRight: 2,
    borderTop: '1.2px solid rgb(183, 185, 195)',
    borderRight: '1.2px solid rgb(183, 185, 195)',
    borderBottom: '1.2px solid rgb(183, 185, 195)',
    justifyContent: 'center',
    display: 'flex',
    alignItems: 'center',
  },
  wordBreak: {
    wordBreak: 'break-all',
  }
}));

type colType = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export interface KVMViewerProps {
  dataList: any | Array<any>;
  emptyText?: string;
  noTitleCase?: boolean;
  controls?: ReactElement;
  enableBorder?: boolean;
  flex?: {
    controls: colType;
    value: colType;
  };
  variant?:
    | 'body1'
    | 'body2'
    | 'inherit'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6';
  color?: string;
  titleStyles?: React.CSSProperties;
}

interface KVMProps {
  title: string;
  value?: any;
  meta?: {
    type?: 'info' | 'help';
    title?: string;
    message?: string;
  };
}
export const KVMViewer: React.FC<KVMViewerProps> = React.memo(
  ({
    dataList,
    emptyText = 'Not Available',
    noTitleCase,
    controls,
    enableBorder = false,
    flex = {
      controls: controls ? 1 : 0 as colType,
      value: 8 as colType,
    },
    variant = 'body1',
    color,
    titleStyles = {}
  }) => {
    const theme = useTheme();
    const classes = useStyles();

    const TableRow = ({ title, value, meta }: KVMProps) => (
      <Grid
        container
        className={clsx([{ border: enableBorder }, 'row'])}
      >
        <Grid item md={12 - flex.value as any} xs={12} className={clsx("title", classes.titleCell)}>
          <Box className={classes.titleBox}>
            <Typography
              variant={variant}
              style={{color:color ?? "#000", textTransform: noTitleCase ? "lowercase" : "none", ...titleStyles}}
            >
              {title.charAt(0).toUpperCase() + title.slice(1)}
            </Typography>
          </Box>
        </Grid>
        <Grid item md={flex.value as GridSize} xs={12} className={classes.valueCell}>
          {typeof value === 'string' && (
            <Typography
              color={value ? 'textPrimary' : 'textSecondary'}
              className={classes.wordBreak}
            >
              {value ?? emptyText}
            </Typography>
          )}
          {typeof value !== 'string' && (value ?? emptyText)}
        </Grid>
      </Grid>
    );
    return (
      <Grid container>
        <Grid item md={12 - flex.controls as GridSize} className={classes.borderedRow}>
          <Grid container>
            {Object.keys(dataList).map((key, index) => (
              <TableRow
                key={index}
                title={key}
                value={dataList?.[key]?.['value'] ?? dataList?.[key] ?? ""}
                meta={dataList?.[key]?.['meta']}
              />
            ))}
          </Grid>
        </Grid>
        {controls && (
            //          <Grid item md={flex.controls as GridSize} style={{paddingRight:2, display:'flex', justifyContent: 'center', alignItems: 'center', border: `1px solid rgb(183, 185, 195)`, borderLeftWidth: "0"}}>
          <Grid item md={flex.controls as GridSize} className={classes.controlsContainer}>
            {controls}
          </Grid>
        )}
      </Grid>
    );
  }
);

export default KVMViewer;
